import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../../api';
import { io } from 'socket.io-client';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { formatNoticeTime, normalizeNotice } from '../../utils/notices';
import { getSocketUrl } from '../../utils/socket';

const AdminDashboard = () => {
    const [stats, setStats] = useState({});
    const [attendanceData, setAttendanceData] = useState([]);
    const [assignments, setAssignments] = useState([]);
    const [notices, setNotices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const fetchData = useCallback(async () => {
        try {
            const [res, attendanceRes, assignmentRes, noticeRes] = await Promise.all([
                api.get('/users/summary'),
                api.get('/attendance/today-summary'),
                api.get('/assignments'),
                api.get('/notices')
            ]);

            setStats(res.data);
            setAttendanceData((attendanceRes.data.summary || []).map(item => ({
                className: `${item.class_name} ${item.section_name}`,
                present: parseInt(item.present_count || 0),
                absent: parseInt(item.absent_count || 0)
            })));
            setAssignments((assignmentRes.data.assignments || []).slice(0, 5));
            setNotices((noticeRes.data.notices || []).map(normalizeNotice).slice(0, 6));
            setError('');
        } catch (err) {
            console.error(err);
            setError('Dashboard notifications could not be refreshed.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();

        const socket = io(getSocketUrl(), {
            transports: ['websocket'],
            reconnection: true
        });

        socket.on('attendance-dashboard-updated', () => {
            console.log('Real-time dashboard refresh triggered!');
            fetchData();
        });

        socket.on('notice-board-updated', () => {
            console.log('Notice board refresh triggered!');
            fetchData();
        });

        return () => socket.disconnect();
    }, [fetchData]);

    const newNoticeCount = useMemo(() => notices.filter(notice => notice.isNew).length, [notices]);
    const totalPresent = attendanceData.reduce((sum, item) => sum + item.present, 0);
    const totalAbsent = attendanceData.reduce((sum, item) => sum + item.absent, 0);

    return (
        <div className="admin-dashboard">
            <div className="d-flex justify-content-between align-items-start mb-4 flex-wrap gap-3">
                <div>
                    <h3 className="fw-bold m-0"><i className="bi bi-speedometer2 me-2 text-primary"></i>Admin Dashboard</h3>
                    <p className="text-muted small mb-0 mt-1">Live attendance, notices, and staff activity in one view</p>
                </div>
                <Link to="/notices" className="btn btn-dark rounded-3 fw-bold px-4">
                    <i className="bi bi-megaphone-fill me-2"></i>Manage Notices
                </Link>
            </div>

            {error && <div className="alert alert-warning border-0 rounded-3 shadow-sm small fw-medium">{error}</div>}

            <div className="row g-4 mb-4">
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100">
                        <div className="card-body">
                            <h6 className="text-muted small fw-bold mb-1">Total Students</h6>
                            <h3 className="fw-bold mb-0">{stats.studentCount || 0}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100">
                        <div className="card-body">
                            <h6 className="text-muted small fw-bold mb-1">Total Teachers</h6>
                            <h3 className="fw-bold mb-0">{stats.teacherCount || 0}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100 border-start border-warning border-4">
                        <div className="card-body">
                            <h6 className="text-muted small fw-bold mb-1">New Notices</h6>
                            <h3 className="fw-bold mb-0">{newNoticeCount}</h3>
                        </div>
                    </div>
                </div>
                <div className="col-md-3">
                    <div className="card border-0 shadow-sm rounded-4 p-3 bg-white h-100 border-start border-success border-4">
                        <div className="card-body">
                            <h6 className="text-muted small fw-bold mb-1">Today Present / Absent</h6>
                            <h3 className="fw-bold mb-0">{totalPresent}<span className="text-muted fs-6 fw-medium"> / {totalAbsent}</span></h3>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-4">
                <div className="col-lg-7">
                    <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-4">
                        <div className="d-flex justify-content-between align-items-center mb-4">
                            <h6 className="fw-bold mb-0">Real-time Attendance Overview</h6>
                            {loading && <span className="spinner-border spinner-border-sm text-primary" role="status"></span>}
                        </div>
                        <div style={{ height: '300px' }}>
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={attendanceData}>
                                    <defs>
                                        <linearGradient id="colorPresent" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#0d6efd" stopOpacity={0.1}/><stop offset="95%" stopColor="#0d6efd" stopOpacity={0}/></linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f1f1" />
                                    <XAxis dataKey="className" axisLine={false} tickLine={false} />
                                    <YAxis axisLine={false} tickLine={false} />
                                    <Tooltip />
                                    <Area type="monotone" dataKey="present" stroke="#0d6efd" fillOpacity={1} fill="url(#colorPresent)" strokeWidth={3} />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </div>
                </div>

                <div className="col-lg-5">
                    <div className="card border-0 shadow-sm rounded-4 p-4 bg-white mb-4 border-start border-warning border-5">
                        <div className="d-flex justify-content-between align-items-start gap-3 mb-4">
                            <div>
                                <h6 className="fw-bold mb-1"><i className="bi bi-bell-fill me-2 text-warning"></i>Notification Center</h6>
                                <div className="text-muted small fw-medium">Recent notices visible to students and staff</div>
                            </div>
                            <span className={`badge rounded-pill px-3 py-2 ${newNoticeCount > 0 ? 'bg-warning text-dark' : 'bg-light text-muted border'}`}>
                                {newNoticeCount} New
                            </span>
                        </div>

                        <div className="d-flex flex-column gap-3">
                            {notices.length > 0 ? notices.map(notice => (
                                <div key={notice.id} className="notice-row pb-3 border-bottom border-light">
                                    <div className="d-flex align-items-start gap-3">
                                        <div className={`rounded-3 d-flex align-items-center justify-content-center flex-shrink-0 ${notice.isNew ? 'bg-warning text-dark' : 'bg-light text-muted'}`} style={{width: '40px', height: '40px'}}>
                                            <i className="bi bi-megaphone-fill"></i>
                                        </div>
                                        <div className="flex-grow-1 min-w-0">
                                            <div className="d-flex align-items-center gap-2 mb-1">
                                                <h6 className="fw-bold mb-0 text-dark line-clamp-1">{notice.title}</h6>
                                                {notice.isNew && <span className="badge bg-warning-subtle text-warning-emphasis rounded-pill">New</span>}
                                            </div>
                                            <div className="small text-muted fw-bold mb-2">{formatNoticeTime(notice.createdAt)} by {notice.authorName}</div>
                                            <p className="small text-muted mb-0 line-clamp-2">{notice.body || 'No details provided.'}</p>
                                        </div>
                                    </div>
                                </div>
                            )) : (
                                <div className="text-center p-4 bg-light rounded-4 text-muted small fw-bold">
                                    <i className="bi bi-bell-slash fs-2 d-block mb-2 opacity-50"></i>
                                    No notices published yet.
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="card border-0 shadow-sm rounded-4 p-4 bg-white">
                        <h6 className="fw-bold mb-3 text-dark">Live Teacher Activity</h6>
                        <div className="d-flex flex-column gap-3">
                            {assignments.length > 0 ? assignments.map(task => (
                                <div key={task.id} className="p-3 bg-light rounded-3 border-0">
                                    <div className="d-flex justify-content-between small mb-1 gap-2">
                                        <span className="fw-bold text-primary">{task.class_name} - {task.course_name}</span>
                                        <span className="text-muted text-nowrap">{task.teacher_first} {task.teacher_last}</span>
                                    </div>
                                    <h6 className="fw-bold mb-1 small">{task.title}</h6>
                                </div>
                            )) : (
                                <div className="text-center p-4 bg-light rounded-4 text-muted small fw-bold">No recent teacher activity.</div>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <style>{`
                .line-clamp-1 { display: -webkit-box; -webkit-line-clamp: 1; -webkit-box-orient: vertical; overflow: hidden; }
                .line-clamp-2 { display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden; }
                .notice-row:last-child { border-bottom: 0 !important; padding-bottom: 0 !important; }
            `}</style>
        </div>
    );
};

export default AdminDashboard;
